import React, { useEffect, useMemo, useRef, useState } from "react";
import "./OneOSLoginShell.css";

/**
 * OneOSLoginShell — Universal 1OS engine login surface.
 * =====================================================
 *
 * Single source of truth for every engine login screen (INV/SAL/ACC/EXP/HR/CTRL).
 * Visual parity across engines is guaranteed by construction, not by convention.
 *
 * Locked design rules (do NOT override in consumers):
 *   - Continuous ambient canvas. Login card floats center-left on desktop.
 *   - Ambient outcome storytelling renders on the RIGHT of the canvas on desktop
 *     (z:2, above engine watermark, below the card) and INLINE between the logo
 *     and the card on mobile/tablet.
 *   - Amber accent #F5C518. Surfaces #08080A / #0C0C0E / #111113.
 *   - Font: Outfit. Weights: 300 / 400 / 500 ONLY.
 *   - Dwell: 2500ms desktop, 3500ms mobile. Fade: 300ms
 *     (cubic-bezier(0.22, 0.61, 0.36, 1)).
 *   - Motion: opacity + translateY(-6px) on exit (desktop). No slide/scale/typewriter.
 *   - Enter submits; loader stays inside button; inline error; no layout shift.
 *   - Giant engine code watermark on desktop only (z:1), card at z:3 above.
 *
 * Consumers provide:
 *   - engineCode / engineFull (from /src/config/engines.js ENGINES map)
 *   - onSubmit(email, password) -> Promise<{ success: boolean, error?: string }>
 *   - submitting / error (optional — shell can manage its own if not passed)
 *   - messages (optional engine-specific outcome lines; 8 defaults provided)
 */

const LOGO_SRC = "/brand/1os-logo.png";

// Default outcome-led strings — not features. Round-robin.
const DEFAULT_MESSAGES = [
  "Every number ties back to a source. Nothing is assumed.",
  "Revenue, cost, and cash all reconcile.",
  "You don't guess margins. You see what actually drove them.",
  "Settlements match. Cash is real, not projected.",
  "Forecasts are bounded. Reality stays in view.",
  "Every variance has a reason. You see it before it compounds.",
  "The books match the bank. The bank matches the business.",
  "One system. One source of truth.",
];

const VISIBLE_MS_DESKTOP = 2500;
const VISIBLE_MS_MOBILE = 3500;
const FADE_MS = 300;

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

const isDesktopViewport = () =>
  typeof window !== "undefined" &&
  window.matchMedia &&
  window.matchMedia("(min-width: 1024px)").matches;

export const OneOSLoginShell = ({
  engineCode,
  engineFull,
  messages,
  onSubmit,
  error: errorProp,
  submitting: submittingProp,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [internalSubmitting, setInternalSubmitting] = useState(false);
  const [internalError, setInternalError] = useState("");
  const [messageIndex, setMessageIndex] = useState(0);
  const [exiting, setExiting] = useState(false);
  const emailRef = useRef(null);

  const submitting = submittingProp ?? internalSubmitting;
  const error = errorProp ?? internalError;
  const lines = useMemo(
    () => (Array.isArray(messages) && messages.length > 0 ? messages : DEFAULT_MESSAGES),
    [messages],
  );

  // Auto-focus email on mount
  useEffect(() => {
    if (emailRef.current) emailRef.current.focus();
  }, []);

  // Outcome storytelling rotation.
  // Reduced-motion: cycle stops, first line stays.
  useEffect(() => {
    if (lines.length <= 1) return;
    if (prefersReducedMotion()) return;
    const dwell = isDesktopViewport() ? VISIBLE_MS_DESKTOP : VISIBLE_MS_MOBILE;
    const tick = setInterval(() => {
      setExiting(true);
      setTimeout(() => {
        setMessageIndex((i) => (i + 1) % lines.length);
        setExiting(false);
      }, FADE_MS);
    }, dwell);
    return () => clearInterval(tick);
  }, [lines]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (submitting) return;
    setInternalError("");
    if (submittingProp === undefined) setInternalSubmitting(true);
    try {
      const result = await onSubmit?.(email, password);
      if (result && !result.success && errorProp === undefined) {
        setInternalError(result.error || "Sign-in failed.");
      }
    } catch (ex) {
      if (errorProp === undefined) setInternalError(ex?.message || "Sign-in failed.");
    } finally {
      if (submittingProp === undefined) setInternalSubmitting(false);
    }
  };

  const currentLine = lines[messageIndex];

  return (
    <div className="oneos-shell" data-testid="oneos-login-shell">
      {/* Ambient canvas — z:0 */}
      <div className="ambient-vignette" aria-hidden="true" />
      <div className="ambient-amber" aria-hidden="true" />
      <div className="ambient-grid" aria-hidden="true" />
      <div className="ambient-grain" aria-hidden="true" />

      {/* Engine watermark — z:1, desktop only */}
      <div
        className="ambient-engine-mark hidden lg:flex"
        aria-hidden="true"
        data-testid="oneos-engine-mark"
      >
        {engineCode}
      </div>

      {/* Ambient outcome storytelling — desktop right-aligned layer (z:2) */}
      <div className="oneos-shell__ambient" aria-hidden="true">
        <div
          className={`oneos-shell__ambient-text${exiting ? " is-exiting" : ""}`}
          data-testid="oneos-ambient-message-desktop"
        >
          {currentLine}
        </div>
      </div>

      {/* Content — z:3 */}
      <div className="oneos-shell__content">
        {/* Logo row */}
        <div className="oneos-shell__logo-row" data-testid="oneos-engine-badge">
          <img
            src={LOGO_SRC}
            alt="1OS"
            className="oneos-shell__logo-mark-img"
            draggable="false"
          />
          <span className="oneos-shell__logo-engine">{engineFull}</span>
        </div>

        {/* Mobile/tablet inline ambient message — between logo and card */}
        <div
          className={`oneos-shell__ambient-inline${exiting ? " is-exiting" : ""}`}
          aria-hidden="true"
          data-testid="oneos-ambient-message-mobile"
        >
          {currentLine}
        </div>

        {/* Card */}
        <form
          className="oneos-shell__card"
          onSubmit={handleSubmit}
          data-testid="oneos-login-card"
          noValidate
        >
          <h1 className="oneos-shell__card-title">Sign in</h1>
          <p className="oneos-shell__card-subtitle">Continue to {engineFull}.</p>

          <label className="oneos-shell__label" htmlFor="oneos-email">Email</label>
          <input
            id="oneos-email"
            ref={emailRef}
            className="oneos-shell__field"
            type="email"
            autoComplete="email"
            autoCapitalize="off"
            spellCheck="false"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            disabled={submitting}
            required
            data-testid="oneos-login-email"
          />

          <label className="oneos-shell__label" htmlFor="oneos-password">Password</label>
          <input
            id="oneos-password"
            className="oneos-shell__field"
            type="password"
            autoComplete="current-password"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            disabled={submitting}
            required
            data-testid="oneos-login-password"
          />

          {/* Reserved space — no layout shift when error appears */}
          <div
            className="oneos-shell__error"
            role="alert"
            aria-live="polite"
            data-testid="oneos-login-error"
          >
            {error || "\u00a0"}
          </div>

          <button
            type="submit"
            className="oneos-shell__submit"
            disabled={submitting}
            data-testid="oneos-login-submit"
          >
            {submitting && <span className="oneos-shell__submit-spinner" aria-hidden="true" />}
            {submitting ? "Signing in" : "Sign in"}
          </button>

          <p className="oneos-shell__footer-note">Control Room Identity</p>
        </form>
      </div>

      <div className="oneos-shell__footer" data-testid="oneos-footer">1OS</div>
    </div>
  );
};

export default OneOSLoginShell;
